import type { VerificationRequestState, VerificationSubmission } from '@k2/domain/identity'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useVerificationResource } from '../resources'

export type Verification = {
  state: VerificationRequestState
  requestId?: string
  submittedAt?: Date
  reviewedAt?: Date
  reviewerNotes?: string
  resubmitAllowed: boolean
  company?: VerificationSubmission
}

// What the route refuses with (FR-A9, FR-A11); `failed` is the catch-all, so an unnamed error still reaches the surface (NFR-A1).
export type VerificationRefusal = 'verified' | 'pending' | 'closed' | 'failed'

export type QueueEntry = {
  requestId: string
  organizationId: string
  organizationName: string
  state: VerificationRequestState
  submittedAt: Date
  requester?: { email: string; name: string }
  company?: VerificationSubmission
}

export type ReviewDecision = 'approve' | 'reject' | 'more_information'

export const useVerificationStore = defineStore('verification', () => {
  const { getVerification, setVerification, getVerificationRequests, setVerificationRequestReview } = useVerificationResource()
  const verification = ref<Verification>()
  const pending = ref(false)
  const refusal = ref<VerificationRefusal>()

  const load = async (organizationId: string) => {
    const { data } = await getVerification(organizationId)
    verification.value = data ?? undefined
  }

  const submit = async (organizationId: string, submission: VerificationSubmission) => {
    pending.value = true
    refusal.value = undefined
    try {
      const { data, error } = await setVerification(organizationId, submission)
      if (error) {
        refusal.value = (error.value as { error?: VerificationRefusal })?.error ?? 'failed'
        return false
      }
      verification.value = data ?? undefined
      return true
    } finally {
      pending.value = false
    }
  }

  const queue = ref<QueueEntry[]>([])

  const loadQueue = async () => {
    const { data } = await getVerificationRequests()
    queue.value = data ?? []
  }

  const review = async (requestId: string, decision: ReviewDecision, notes?: string, resubmitAllowed?: boolean) => {
    pending.value = true
    try {
      const { error } = await setVerificationRequestReview(requestId, { decision, notes, resubmitAllowed })
      if (error) return false
      await loadQueue()
      return true
    } finally {
      pending.value = false
    }
  }

  return { verification, pending, refusal, queue, load, submit, loadQueue, review }
})
